import { memo, type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import { Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Only http(s), mailto, same-origin root-relative paths and fragments survive.
 * Anything else (javascript:, data:, vbscript:, protocol-relative) is dropped.
 */
export function safeUrl(url: string): string | undefined {
  const u = url.trim();
  if (/^(https?:|mailto:)/i.test(u)) return u;
  if (u.startsWith("#")) return u;
  if (u.startsWith("/") && !u.startsWith("//")) return u;
  return undefined;
}

function isExternal(href: string): boolean {
  return /^(https?:|mailto:)/i.test(href);
}

/** Images load only from Minne's own file store; everything else is a link chip. */
function isOwnFile(src: string): boolean {
  try {
    const u = new URL(src, window.location.href);
    return u.origin === window.location.origin && u.pathname.startsWith("/api/files/");
  } catch {
    return false;
  }
}

function imageLabel(src: string): string {
  try {
    const u = new URL(src, window.location.href);
    const path = u.pathname.length > 40 ? u.pathname.slice(0, 37) + "…" : u.pathname;
    return `${u.host}${path}`;
  } catch {
    return src.length > 48 ? src.slice(0, 45) + "…" : src;
  }
}

const components: Components = {
  a({ href, children, title, ...rest }) {
    const h = href ?? "";
    if (!h) return <span {...rest}>{children}</span>;
    if (isExternal(h)) {
      return (
        <a href={h} target="_blank" rel="noopener noreferrer" title={title ?? h} {...rest}>
          {children}
        </a>
      );
    }
    if (h.startsWith("#")) {
      return (
        <a href={h} title={title} {...rest}>
          {children}
        </a>
      );
    }
    return (
      <Link to={h} title={title}>
        {children}
      </Link>
    );
  },
  img({ src, alt }) {
    const s = src ?? "";
    if (s && isOwnFile(s)) {
      return <img className="md-image" src={s} alt={alt ?? ""} loading="lazy" />;
    }
    const label = s ? `image: ${imageLabel(s)}` : "image";
    if (s && isExternal(s)) {
      return (
        <a className="md-image-chip" href={s} target="_blank" rel="noopener noreferrer" title={s}>
          <ImageIcon className="size-3.5" aria-hidden />
          <span>{alt ? `${alt} — ${label}` : label}</span>
        </a>
      );
    }
    return (
      <span className="md-image-chip" title={s || undefined}>
        <ImageIcon className="size-3.5" aria-hidden />
        <span>{alt ? `${alt} — ${label}` : label}</span>
      </span>
    );
  },
  table({ children, ...rest }) {
    return (
      <div className="table-wrap">
        <table {...rest}>{children}</table>
      </div>
    );
  },
  li({ className, children, ...rest }) {
    const task = typeof className === "string" && className.includes("task-list-item");
    return (
      <li className={cn(className, task && "task-item")} {...rest}>
        {children}
      </li>
    );
  },
  input({ type, checked, ...rest }) {
    if (type === "checkbox") {
      return <input type="checkbox" checked={!!checked} readOnly disabled aria-label={checked ? "done" : "to do"} {...rest} />;
    }
    return null;
  },
};

export interface MarkdownProps {
  children: string;
  className?: string;
  /** Render inline: unwrap the outer paragraph (for list items, chips, titles). */
  inline?: boolean;
  /** Compact prose scale for component surfaces. */
  compact?: boolean;
}

export const Markdown = memo(function Markdown({ children, className, inline, compact }: MarkdownProps) {
  const comps: Components = inline
    ? { ...components, p: ({ children }: { children?: ReactNode }) => <>{children}</> }
    : components;
  const Wrapper = inline ? "span" : "div";
  return (
    <Wrapper className={cn(!inline && "note-prose", compact && "note-prose-sm", className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml urlTransform={safeUrl} components={comps}>
        {children}
      </ReactMarkdown>
    </Wrapper>
  );
});
